import { customerSettingsFragment, orgAddressFragment } from 'apollo/fragments';
import {
    labInvoiceSettingsFragment,
    organizationLabSettingsFragment,
    orgCurrentJobFragment,
    listViewProfileFragment,
    orderListProfileDefaultsFragment,
} from 'apollo/fragments/organization';
import { gql } from 'graphql.macro';

export const getAllClinicNamesConnectedWithLabTemplate = gql`
    query allClinicNamesConnectedWithLab($filters: OrganizationsFilters!) {
        clinicsForLab(filters: $filters) {
            _id
            name
        }
    }
`;

export const getParentClinicConnectedWithLabTemplate = gql`
    query parentClinicsForLab {
        parentClinicsForLab {
            _id
            name
        }
    }
`;

export const getAllClinicsForLabNoSettingsTemplate = gql`
    query allClinicsForLab($filters: OrganizationsFilters!) {
        clinicsForLab(filters: $filters) {
            _id
            name
            local
            isMain
            parentClinicId
            isWhiteLabeled
            contact {
                address {
                    ...orgAddressFields
                }
            }
        }
    }
    ${orgAddressFragment}
`;

// Minimal clinic list for dropdowns (e.g. the delivery-schedule customer filter). Selects only
// the fields the option list renders — no customerSettings/address — so a lab with ~10k customers
// returns a small payload instead of ~15 MB.
export const getClinicsForLabDropdownTemplate = gql`
    query clinicsForLabDropdown($filters: OrganizationsFilters!) {
        clinicsForLab(filters: $filters) {
            _id
            name
            local
            isMain
            parentClinicId
        }
    }
`;

export const getClinicsForLabTemplate = gql`
    query clinicsForLab($filters: OrganizationsFilters!) {
        clinicsForLab(filters: $filters) {
            _id
            local
            parentClinicId
            name
            customerType
            isMain
            active
            invoiceType
            invoicePeriod
        }
    }
`;

export const getPtjClinicsTemplate = gql`
    query ptjClinics(
        $filters: OrganizationsFilters
        $limit: Int
        $offset: Int
        $orderBy: String
        $orderDirection: OrderDirection
    ) {
        ptjClinics(
            filters: $filters
            limit: $limit
            offset: $offset
            orderBy: $orderBy
            orderDirection: $orderDirection
        ) {
            organizationDbId
            existsInDb
            hitHorizonsId
            name
            type
            isAlreadyConnected
            organizationNumber
            address {
                ...orgAddressFields
            }
            site
            vatNumber
            costCenter
            isWhiteLabeled
        }
    }
    ${orgAddressFragment}
`;

export const getClinicTemplate = gql`
    query clinicForOrder($id: MongoDbObjectId!) {
        clinic(id: $id) {
            _id
            name
            orderVersion
            parentClinicId
            parentClinic {
                name
                customerSettings {
                    ...customerSettingsFields
                }
            }
            isWhiteLabeled
            contact {
                address {
                    ...orgAddressFields
                }
            }
            customerSettings {
                ...customerSettingsFields
            }
            local
        }
    }
    ${orgAddressFragment}
    ${customerSettingsFragment}
`;

export const getOrganizationByExtIdTemplate = gql`
    query organizationByExtId($extIdField: String!, $extIdValue: String!) {
        organizationByExtId(extIdField: $extIdField, extIdValue: $extIdValue) {
            _id
            name
            parentClinicId
            parentClinic {
                name
                customerSettings {
                    ...customerSettingsFields
                }
            }
            isWhiteLabeled
            contact {
                address {
                    ...orgAddressFields
                }
            }
            customerSettings {
                ...customerSettingsFields
            }
            local
        }
    }
    ${orgAddressFragment}
    ${customerSettingsFragment}
`;

export const labSettingsTemplate = gql`
    query currentLab {
        currentLab {
            ...organizationLabSettingsFields
        }
    }
    ${organizationLabSettingsFragment}
`;

export const labInvoiceSettingsTemplate = gql`
    query currentLab {
        currentLab {
            ...labInvoiceSettingsFields
        }
    }
    ${labInvoiceSettingsFragment}
`;

export const getRelatedClinicsTemplate = gql`
    query relatedClinics($clinicId: MongoDbObjectId!, $limit: Int, $offset: Int) {
        relatedClinics(clinicId: $clinicId, limit: $limit, offset: $offset) {
            internalDeliveryDaysAdvance
            externalDeliveryDaysAdvance
            deliveryInstructions
            transportProvider {
                channel
                externalId
                code
            }
            customer {
                _id
                name
                type
                parentClinicId
                parentClinic {
                    name
                }
                active
                isMain
                customerType
                contact {
                    address {
                        ...orgAddressFields
                    }
                }
            }
        }
    }
    ${orgAddressFragment}
`;

export const organizationLookupTemplate = gql`
    query organizationLookup(
        $countryCode: String!
        $organizationNumber: String!
        $checkWhitelabel: Boolean!
        $type: String
        $existingOnly: Boolean
    ) {
        organizationLookup(
            countryCode: $countryCode
            organizationNumber: $organizationNumber
            checkWhitelabel: $checkWhitelabel
            type: $type
            existingOnly: $existingOnly
        ) {
            organizationDbId
            existsInDb
            hitHorizonsId
            name
            type
            isAlreadyConnected
            connectionIsActive
            organizationNumber
            address {
                ...orgAddressFields
            }
            site
            vatNumber
            costCenter
            isWhiteLabeled
        }
    }
    ${orgAddressFragment}
`;

export const waybillPackageSizesTemplate = gql`
    query waybillPackageSizes {
        waybillPackageSizes {
            _id
            name
            width
            height
            length
        }
    }
`;

export const connectedLabsForClinicTemplate = gql`
    query connectedLabsForClinic {
        connectedLabsForClinic {
            _id
            lab {
                _id
                name
                logoSignedUrl
                contact {
                    address {
                        ...orgAddressFields
                    }
                    communication {
                        phone
                        email
                        site
                    }
                }
                currentJobs {
                    ...orgCurrentJobFields
                }
                openingHours {
                    day
                    time {
                        from
                        to
                    }
                    active
                }
                holidayDays
                customerSettings {
                    ...customerSettingsFields
                }
            }
        }
    }
    ${orgAddressFragment}
    ${orgCurrentJobFragment}
    ${customerSettingsFragment}
`;

export const getLabForClinicTemplate = gql`
    query lab($id: MongoDbObjectId!) {
        lab(id: $id) {
            _id
            name
            logoSignedUrl
            contact {
                address {
                    ...orgAddressFields
                }
                communication {
                    phone
                    email
                    site
                }
            }
            currentJobs {
                ...orgCurrentJobFields
            }
            openingHours {
                day
                time {
                    from
                    to
                }
                active
            }
            holidayDays
            customerSettings {
                ...customerSettingsFields
            }
        }
    }
    ${orgAddressFragment}
    ${orgCurrentJobFragment}
    ${customerSettingsFragment}
`;

export const connectedLabsListTemplate = gql`
    query labsForClinic(
        $notConnected: Boolean
        $searchText: SearchString
        $onlyPublishedLabs: Boolean
        $limit: Int
        $offset: Int
    ) {
        labsForClinic(
            notConnected: $notConnected
            searchText: $searchText
            onlyPublishedLabs: $onlyPublishedLabs
            limit: $limit
            offset: $offset
        ) {
            _id
            name
            shortDescription
            longDescription
            staffImage
            logoSignedUrl
            staffSignedUrl
            openingHours {
                active
                day
            }
            contact {
                address {
                    street
                    postalCode
                    city
                    country
                }
                communication {
                    site
                    email
                    phone
                }
            }
            currentJobs {
                name
                active
            }
            openingHours {
                active
                day
                time {
                    from
                    to
                }
            }
        }
    }
`;

export const salesforceUsersTemplate = gql`
    query salesforceUsers {
        salesforceUsers {
            id
            name
            email
        }
    }
`;

export const getChildClinicsNamesWithParentTemplate = gql`
    query childClinicsWithParent($parentClinicId: MongoDbObjectId, $showOnlyDeliveryLocations: Boolean) {
        childClinicsWithParent(parentClinicId: $parentClinicId, showOnlyDeliveryLocations: $showOnlyDeliveryLocations) {
            _id
            name
            contact {
                address {
                    ...orgAddressFields
                }
            }
        }
    }
    ${orgAddressFragment}
`;

export const denthubSettingsTemplate = gql`
    query denthubSettings {
        denthubSettings {
            _id
            authAppBanner {
                title
                message
                severity
                isEnabled
            }
            labAppBanner {
                title
                message
                severity
                isEnabled
            }
            clinicAppBanner {
                title
                message
                severity
                isEnabled
            }
        }
    }
`;

export const clinicConnectedLabsTemplate = gql`
    query ClinicConnectedLabs($sortOrder: String, $searchText: String, $status: String, $limit: Int, $offset: Int) {
        clinicConnectedLabs(
            sortOrder: $sortOrder
            searchText: $searchText
            status: $status
            limit: $limit
            offset: $offset
        ) {
            _id
            connectedLabs {
                _id
                categoryNames
                isDisabled
                lab {
                    _id
                    logoSignedUrl
                    name
                    organizationNumber
                }
                ratePercent
                totalRating
            }
        }
    }
`;

export const AllLabsForClinicTemplate = gql`
    query AllLabsForClinic(
        $sortOrder: String
        $sortBy: String
        $searchText: SearchString
        $onlyPublishedLabs: Boolean
        $limit: Int
        $offset: Int
    ) {
        allLabsForClinic(
            sortOrder: $sortOrder
            sortBy: $sortBy
            searchText: $searchText
            onlyPublishedLabs: $onlyPublishedLabs
            limit: $limit
            offset: $offset
        ) {
            isAlreadyConnected
            lab {
                _id
                organizationNumber
                name
                contact {
                    address {
                        city
                        country
                        postalCode
                        street
                        street2
                    }
                }
            }
        }
    }
`;

export const GetClinicCategoriesTemplate = gql`
    query GetClinicCategories {
        getClinicCategories {
            _id
            name
            actionSet {
                _id
                title
            }
        }
    }
`;
export const ConnectedLabForClinicTemplate = gql`
    query ConnectedLabForClinic($labId: MongoDbObjectId!, $actionsetId: MongoDbObjectId, $categoryId: MongoDbObjectId) {
        connectedLabForClinic(labId: $labId, actionsetId: $actionsetId, categoryId: $categoryId) {
            _id
            actionSets {
                _id
                title
                currency
            }
            activeActionSet
            materialGroups {
                _id
                materials {
                    _id
                    material
                }
                actionSet
                name
            }
            categories {
                _id
                name
                actionSet
                isSelected
            }
            actions {
                _id
                name
                number
                quotable
            }
            connectedLab {
                connectedDate
                isDisabled
                isRequireException
                isTioTandvardEnabled
                currency
                lab {
                    _id
                    name
                    logoSignedUrl
                    organizationNumber
                    contact {
                        address {
                            city
                            country
                            postalCode
                            street
                            street2
                            stateProvince
                            deliveryInstructions
                        }
                        communication {
                            phone
                            email
                            site
                        }
                    }
                }
                materialPriceDifference {
                    materials {
                        priceDiff
                        material
                        materialName
                        isAvailable
                    }
                    materialGroupId
                    materialGroupName
                    actionSet
                }
                priceList {
                    actions {
                        enable
                        deliveryTime
                        price
                        tiotandvardPrice
                        action
                    }
                    category
                    actionSet
                    rank
                }
            }
        }
    }
`;

export const labAvailabilityTemplate = gql`
    query LabAvailability($months: [String], $year: String, $labId: MongoDbObjectId!) {
        labAvailability(months: $months, year: $year, labId: $labId) {
            lab {
                _id
                logoSignedUrl
                name
                unavailableLabDates
            }
            monthlyAvailability {
                availableDays
                availablityPercent
                month
                monthlyDays
                unavailableDates
                holidayDates
                availableDates
                startDate
            }
            year
            connectYear
        }
    }
`;

export const getProductionPlanningTemplatesTemplate = gql`
    query ProductionPlanningTemplates {
        productionPlanningTemplates {
            _id
            name
            workflowSteps {
                _id
                stepId
                name
                stepOrder
                departmentId
                machineId
                estimatedProductionHours
                estimatedMachineHours
                allowOvernight
                startNextDay
                affectedByQuantityModifier
            }
        }
    }
`;

export const getProductionValueTemplatesTemplate = gql`
    query ProductionValueTemplates {
        productionValueTemplates {
            _id
            name
            steps {
                _id
                stepId
                name
                stepOrder
                valuePercent
            }
        }
    }
`;

export const getListViewProfilesTemplate = gql`
    query ListViewProfiles {
        listViewProfiles {
            ...listViewProfileFields
        }
        orderListProfileDefaults {
            ...orderListProfileDefaultsFields
        }
    }
    ${listViewProfileFragment}
    ${orderListProfileDefaultsFragment}
`;

export const getProductionValueStepsTemplate = gql`
    query ProductionValueSteps {
        productionValueSteps {
            _id
            name
            departmentId
            workflowStepIds
        }
    }
`;

export const pauseReasonsTemplate = gql`
    query PauseReasons {
        pauseReasons
    }
`;

export const machinesTemplate = gql`
    query Machines {
        machines {
            _id
            name
            defaultMachineTime
            machineSchedule
        }
    }
`;

export const departmentsTemplate = gql`
    query Departments {
        departments {
            _id
            name
            workflowSteps {
                _id
                name
                stepOrder
            }
            technicians {
                _id
                fullName
                name {
                    firstName
                    lastName
                }
            }
            productionHourLimit
            capacity
        }
    }
`;

export const documentBundlesTemplate = gql`
    query DocumentBundles {
        documentBundles {
            _id
            title
            documents {
                documentType
                copies
            }
        }
    }
`;

export const actionBasedClinicsForLabTemplate = gql`
    query ActionBasedClinicsForLab {
        actionBasedClinicsForLab {
            _id
            name
        }
    }
`;

export const labsForCopyPriceListTemplate = gql`
    query LabsForCopyPriceList($categoryId: MongoDbObjectId!, $actionSetId: MongoDbObjectId!) {
        labsForCopyPriceList(categoryId: $categoryId, actionSetId: $actionSetId) {
            _id
            name
            priceList {
                action
                price
                deliveryTime
                tiotandvardPrice
                enable
            }
        }
    }
`;

// DD-3700: whether the lab charges VAT on some actions or materials without a VAT number.
export const labVatNumberWarningTemplate = gql`
    query labVatNumberWarning {
        currentLab {
            _id
            vatNumberMissingForTaxedItems
        }
    }
`;
